<template>
	<div class="opportunity-card elevation-1 round-card background-2 pa-3" @click="$emit('click')">
		<div class="card-header mb-2">
			<span class="opp-name font-weight-medium text-body-2">{{ opportunity.name }}</span>
		</div>

		<div class="card-body">
			<div class="company-name text-caption grey--text mb-1" v-if="opportunity.company_name">
				<v-icon x-small class="mr-1">mdi-domain</v-icon>
				{{ opportunity.company_name }}
			</div>

			<div class="d-flex align-center mb-1" v-if="opportunity.budget">
				<v-icon x-small class="mr-1">mdi-currency-eur</v-icon>
				<span class="text-caption font-weight-medium">{{ formatCurrency(opportunity.budget) }}</span>
			</div>

			<div class="forecast d-flex align-center" v-if="opportunity.forecast">
				<v-progress-linear
					:value="opportunity.forecast"
					height="6"
					rounded
					:color="forecastColor"
					class="mr-2"
					style="max-width: 80px;"
				></v-progress-linear>
				<span class="text-caption">{{ opportunity.forecast }}%</span>
			</div>
		</div>

		<div class="card-footer d-flex justify-space-between align-center mt-2 pt-2" style="border-top: 1px solid rgba(0,0,0,0.1);">
			<div class="expiration text-caption" :class="expirationClass" v-if="opportunity.expiration_date">
				<v-icon x-small class="mr-1">mdi-calendar</v-icon>
				{{ formatDate(opportunity.expiration_date) }}
			</div>
			<div v-else></div>

			<div class="d-flex align-center">
				<!-- Responsabile -->
				<v-tooltip bottom v-if="opportunity.owner_name">
					<template v-slot:activator="{ on }">
						<v-avatar v-if="ownerAvatar" size="24" class="custom-avatar" v-on="on"
							:style="{'background-image': 'url(' + ownerAvatar + ')'}">
						</v-avatar>
						<v-avatar v-else :class="$eventBusUtility.bgColor(opportunity.owner_id)" size="24" v-on="on">
							<span class="white--text text-caption">{{ ownerInitial }}</span>
						</v-avatar>
					</template>
					<span>{{ opportunity.owner_name }}</span>
				</v-tooltip>

				<!-- Separatore -->
				<div v-if="visibleUsers.length" class="ml-1 mr-2" style="width: 1px; height: 16px; background: rgba(0,0,0,0.15)" />

				<!-- Utenti condivisi (max 2) -->
				<v-tooltip bottom v-for="user in visibleUsers" :key="user.id">
					<template v-slot:activator="{ on }">
						<v-avatar v-if="getUserAvatar(user.id)" size="22" class="custom-avatar ml-n1" v-on="on"
							:style="{'background-image': 'url(' + getUserAvatar(user.id) + ')'}">
						</v-avatar>
						<v-avatar v-else :class="$eventBusUtility.bgColor(user.id)" size="22" class="ml-n1" v-on="on">
							<span class="white--text" style="font-size: 10px">{{ user.name.charAt(0) }}</span>
						</v-avatar>
					</template>
					<span>{{ user.name }}</span>
				</v-tooltip>

				<!-- Contatore extra -->
				<v-tooltip bottom v-if="extraUsersCount > 0">
					<template v-slot:activator="{ on }">
						<v-avatar size="22" color="grey lighten-1" class="ml-n1" v-on="on">
							<span class="white--text" style="font-size: 10px">+{{ extraUsersCount }}</span>
						</v-avatar>
					</template>
					<span v-for="(user, i) in extraUsers" :key="user.id">{{ user.name }}<br v-if="i < extraUsers.length - 1"></span>
				</v-tooltip>
			</div>
		</div>
	</div>
</template>

<script>
import moment from 'moment'

export default {
	name: 'OpportunityCard',

	props: {
		opportunity: {
			type: Object,
			required: true
		}
	},

	computed: {
		ownerInitial() {
			if (!this.opportunity.owner_name) return '?'
			return this.opportunity.owner_name.charAt(0).toUpperCase()
		},

		ownerAvatar() {
			const user = this.$store.getters.users[this.opportunity.owner_id]
			return user ? user.avatar : null
		},

		sharedUsers() {
			if (!this.opportunity.users || !this.opportunity.users.length) return []
			return this.opportunity.users.filter(u => u.id !== this.opportunity.owner_id)
		},

		visibleUsers() {
			return this.sharedUsers.slice(0, 2)
		},

		extraUsersCount() {
			return Math.max(0, this.sharedUsers.length - 2)
		},

		extraUsers() {
			return this.sharedUsers.slice(2)
		},

		forecastColor() {
			const forecast = this.opportunity.forecast
			if (forecast >= 70) return 'success'
			if (forecast >= 40) return 'warning'
			return 'error'
		},

		expirationClass() {
			if (!this.opportunity.expiration_date) return ''
			const expDate = moment(this.opportunity.expiration_date)
			const today = moment()
			if (expDate.isBefore(today)) return 'red--text'
			if (expDate.isBefore(today.add(7, 'days'))) return 'orange--text'
			return 'grey--text'
		}
	},

	methods: {
		getUserAvatar(userId) {
			const user = this.$store.getters.users[userId]
			return user ? user.avatar : null
		},

		formatCurrency(value) {
			if (!value) return ''
			return new Intl.NumberFormat('it-IT', {
				style: 'currency',
				currency: 'EUR'
			}).format(value)
		},

		formatDate(date) {
			if (!date) return ''
			return moment(date).format('DD/MM/YYYY')
		}
	}
}
</script>

<style scoped>
.opportunity-card {
	cursor: pointer;
	transition: all 0.2s ease;
	margin-bottom: 8px;
}

.opportunity-card:hover {
	transform: translateY(-2px);
	box-shadow: 0 4px 8px rgba(0,0,0,0.15) !important;
}

.opp-name {
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow: hidden;
	word-break: break-word;
}

.company-name {
	display: -webkit-box;
	-webkit-line-clamp: 1;
	-webkit-box-orient: vertical;
	overflow: hidden;
}
</style>
